
/**
 * The formal quote request. AC-3.1.
 *
 * Single column, labels above inputs, hints above, errors above the field they
 * belong to, and validation on submit rather than while typing. The studio
 * configuration is carried in via sessionStorage so nobody re-enters what they
 * already chose; if someone lands here cold they get a short form instead of a
 * dead end.
 */

import { CheckCircle } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { GarmentMockup } from '@/components/garment-mockup';
import { DRAFT_KEY, type StudioDraft } from '@/components/studio/studio-client';
import { Button, ButtonLink, Callout, EmptyState, Field, inputClass, textareaClass } from '@/components/ui';
import { methodRowToEngine } from '@/lib/data/seed';
import { formatDollars } from '@/lib/money';
import { computeEstimate, locationLabel } from '@/lib/pricing/engine';
import type { PrintMethodRow, Product } from '@/lib/types';

type Errors = Partial<Record<'name' | 'email' | 'form', string>>;

export function QuoteForm({
  products,
  methods,
}: {
  products: Product[];
  methods: PrintMethodRow[];
}) {
  const [draft, setDraft] = useState<StudioDraft | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [ref, setRef] = useState<string | null>(null);
  // The key to the tracking link, handed back with the reference (R-155).
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(DRAFT_KEY);
      if (raw) setDraft(JSON.parse(raw) as StudioDraft);
    } catch {
      // Malformed draft: treat it as no draft.
    }
    setLoaded(true);
  }, []);

  if (!loaded) {
    return <div className="h-72 animate-pulse rounded-[10px] bg-panel" />;
  }

  if (!draft) {
    return (
      <EmptyState
        title="Start in the studio first"
        body="Pick a garment, place your artwork and set the quantity. The quote form picks up from there with everything already filled in."
        action={<ButtonLink href="/custom">Start a custom order</ButtonLink>}
      />
    );
  }

  const product = products.find((p) => p.id === draft.product_id);
  const method = methods.find((m) => m.key === draft.method_key);

  if (!product || !method) {
    return (
      <EmptyState
        title="That configuration is out of date"
        body="The garment or decoration method changed since you set this up. Open the studio again and we will re-price it."
        action={<ButtonLink href="/custom">Start a custom order</ButtonLink>}
      />
    );
  }

  const estimate = computeEstimate(
    product.base_price_cents / 100,
    methodRowToEngine(method),
    { qty: draft.qty, colours: draft.colours, locations: draft.locations },
  );

  if (ref) {
    return (
      <div className="mx-auto max-w-[620px] text-center">
        <CheckCircle size={48} weight="fill" className="mx-auto text-ok" aria-hidden />
        <h1 className="mt-5 text-[32px] font-extrabold tracking-[-0.03em]">
          Quote {ref} is in
        </h1>
        <p className="mt-3 text-[17px] leading-relaxed text-muted">
          We have emailed you a copy of the estimate. Someone will come back with a
          confirmed price and a lead time, usually within one business day.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <ButtonLink href={token ? `/quotes/${ref}?t=${token}` : `/quotes/${ref}`}>
            Track this quote
          </ButtonLink>
          <ButtonLink href="/custom" weight="secondary">
            Start a custom order
          </ButtonLink>
        </div>
      </div>
    );
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = String(form.get('name') ?? '').trim();
    const email = String(form.get('email') ?? '').trim();

    const next: Errors = {};
    if (!name) next.name = 'Tell us who to reply to.';
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      next.email = 'Use an email address we can reply to.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch('/api/quotes-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'web',
          contact: {
            name,
            company: String(form.get('company') ?? '') || undefined,
            email,
            phone: String(form.get('phone') ?? '') || undefined,
          },
          product_id: draft!.product_id,
          method_key: draft!.method_key,
          color: draft!.color,
          size: draft!.size,
          qty: draft!.qty,
          colours: draft!.colours,
          locations: draft!.locations,
          artwork_id: draft!.artwork_id,
          ready_design_id: draft!.ready_design_id,
          design_url: draft!.design_url,
          pos_x: draft!.placement.pos_x,
          pos_y: draft!.placement.pos_y,
          scale: draft!.placement.scale,
          notes: String(form.get('notes') ?? '') || undefined,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setErrors({ form: body?.error?.message ?? 'That did not go through.' });
        return;
      }
      window.sessionStorage.removeItem(DRAFT_KEY);
      setRef(body.ref as string);
      setToken((body.token as string | null) ?? null);
    } catch {
      setErrors({ form: 'That did not go through. Check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  const colorHex =
    product.colors.find((c) => c.name === draft.color)?.hex ?? product.colors[0]!.hex;

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-14">
      <div className="order-2 lg:order-1">
        <h1 className="text-[32px] font-extrabold tracking-[-0.03em] sm:text-[38px]">
          Request a quote
        </h1>
        <p className="mt-2 max-w-[52ch] text-[17px] text-muted">
          We come back with a confirmed price, a lead time, and an artwork proof before
          anything gets printed.
        </p>

        {errors.form && (
          <div className="mt-6">
            <Callout tone="danger" title={errors.form} />
          </div>
        )}

        {/* Single column, four fields, two of them optional. */}
        <form onSubmit={submit} noValidate className="mt-8 max-w-[520px] space-y-6">
          <Field label="Your name" htmlFor="name" error={errors.name}>
            <input
              id="name"
              name="name"
              autoComplete="name"
              className={inputClass}
              aria-invalid={Boolean(errors.name)}
            />
          </Field>

          <Field label="Company" htmlFor="company" optional>
            <input
              id="company"
              name="company"
              autoComplete="organization"
              className={inputClass}
            />
          </Field>

          <Field
            label="Email"
            htmlFor="email"
            error={errors.email}
            hint="We send the confirmed price and the proof here."
          >
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className={inputClass}
              aria-invalid={Boolean(errors.email)}
            />
          </Field>

          <Field label="Phone" htmlFor="phone" optional>
            {/* Narrow field for a short input. */}
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              className={`${inputClass} max-w-[220px]`}
            />
          </Field>

          <Field
            label="Anything we should know"
            htmlFor="notes"
            optional
            hint="Deadlines, size breakdown, pantone references."
          >
            <textarea id="notes" name="notes" rows={4} className={textareaClass} />
          </Field>

          <Button type="submit" size="lg" disabled={submitting}>
            {submitting ? 'Sending' : 'Send quote request'}
          </Button>
        </form>
      </div>

      {/* What they configured, so the form is not a leap of faith. */}
      <aside className="order-1 lg:order-2">
        <div className="rounded-[10px] border border-line bg-panel p-5 lg:sticky lg:top-24">
          <h2 className="text-sm font-bold">Your configuration</h2>
          <div className="checkerboard mt-4 rounded-[10px] border border-line">
            <GarmentMockup
              type={product.type}
              colorHex={colorHex}
              designUrl={draft.design_url}
              placement={draft.placement}
              showZone
              label={`${product.name} in ${draft.color}`}
              className="aspect-square w-full"
            />
          </div>

          <dl className="mt-5 space-y-2 text-[15px]">
            {[
              ['Garment', product.name],
              ['Colour', draft.color],
              ['Size', draft.size],
              ['Decoration', method.name],
              [
                'Locations',
                draft.locations.map((l) => locationLabel(l)).join(', '),
              ],
              ...(estimate.colours != null
                ? ([['Colours', String(estimate.colours)]] as [string, string][])
                : []),
              ['Quantity', String(draft.qty)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt className="text-muted">{k}</dt>
                <dd className="text-right font-semibold">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-5 border-t border-line pt-4">
            <div className="flex items-end justify-between gap-4">
              <span className="text-sm text-muted">Estimated total</span>
              <span className="text-2xl font-extrabold tracking-[-0.02em]">
                {formatDollars(estimate.total)}
              </span>
            </div>
            <p className="mt-1 text-[13px] text-muted">
              {formatDollars(estimate.unit_price)} / unit including GST
            </p>
          </div>

          {!estimate.moq_ok && (
            <div className="mt-4">
              <Callout tone="warn" title={`Below the ${method.name} minimum`}>
                Send it through anyway. We will work out the best option when we price it.
              </Callout>
            </div>
          )}

          <p className="mt-4 text-[13px] text-muted">
            <Link to="/custom" className="font-semibold text-brand-ink hover:underline">
              Change the configuration
            </Link>
          </p>
        </div>
      </aside>
    </div>
  );
}
